import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/shared/ui/shadcn/field';

import { getFieldState } from '@/shared/lib/forms';
import { useMedicationSearchPicker } from '../../model/useMedicationSearchPicker';
import { MedicationSearchDialog } from './MedicationSearchDialog';

export interface IngredientMedicationSelectorProps {
  value: number | null | undefined;
  onChange: (next: { dispensableDrugId: number; dispenseUnitId: number | null }) => void;
  label: string;
  description?: string;
  disabled?: boolean;
  errorMeta?: Parameters<typeof getFieldState>[0];
}

export function IngredientMedicationSelector({
  value,
  onChange,
  label,
  description,
  disabled = false,
  errorMeta,
}: IngredientMedicationSelectorProps) {
  const { isInvalid, errors } = getFieldState(errorMeta);

  const {
    isOpen,
    results,
    isSelecting,
    isSearchTextValid,
    searchQuery,
    searchText,
    searchTextError,
    selectError,
    setSearchText,
    selectMedication,
    setIsOpen,
    selectedLabel,
  } = useMedicationSearchPicker({
    initialSelectedLabel: value != null && value > 0 ? `Medication ${value}` : null,
    onResolve: ({ row, dispenseUnitId }) => {
      onChange({ dispensableDrugId: row.dispensableDrugId, dispenseUnitId });
    },
  });

  const currentLabel =
    selectedLabel ??
    (value != null && Number.isFinite(value) && value > 0 ? `Medication ${value}` : null);

  return (
    <FieldGroup>
      <Field data-invalid={isInvalid}>
        <FieldContent>
          <FieldLabel>{label}</FieldLabel>
          <FieldDescription>
            {description ??
              (searchText.trim().length > 0 && !isSearchTextValid
                ? (searchTextError ?? 'Type at least 3 characters to search medications.')
                : searchQuery.isError
                  ? 'Search failed.'
                  : 'Search DoseSpot medications')}
          </FieldDescription>
          {isInvalid ? <FieldError errors={errors} /> : null}
        </FieldContent>

        <div className="flex w-full flex-col gap-2">
          <MedicationSearchDialog
            open={isOpen}
            onOpenChange={setIsOpen}
            searchText={searchText}
            onSearchTextChange={setSearchText}
            onCloseResetSearchText={() => setSearchText('')}
            isSearchTextValid={isSearchTextValid}
            searchTextError={searchTextError}
            isSelecting={isSelecting}
            isSearchPending={searchQuery.isPending}
            isSearchError={searchQuery.isError}
            onRetrySearch={() => void searchQuery.refetch()}
            results={results}
            onSelectMedication={(row) => void selectMedication(row)}
            selectError={selectError}
            triggerLabel={
              currentLabel ?? (searchText.trim() !== '' ? 'Change medication' : 'Select medication')
            }
            triggerDisabled={disabled || isSelecting}
          />
        </div>
      </Field>
    </FieldGroup>
  );
}
